import { useEffect, useState } from 'react';
import { apiFetch } from '@/client/lib/api';

export interface SkillInfo {
  name: string;
  description: string;
  agents: string[];
  /** Markdown body with frontmatter stripped — embedded directly in user
   *  messages by the slash command flow. */
  body: string;
}

// Module-level cache so multiple components calling `useSkills` (the
// composer, every rendered user message that needs chip rendering, etc.)
// share one fetch per branch instead of each firing their own request.
const _resultCache = new Map<string, SkillInfo[]>();
const _inFlight = new Map<string, Promise<SkillInfo[]>>();

function _cacheKey(branch?: string | null): string {
  return branch ?? '';
}

async function _fetchSkills(branch?: string | null): Promise<SkillInfo[]> {
  const key = _cacheKey(branch);
  const cached = _resultCache.get(key);
  if (cached) return cached;
  const existing = _inFlight.get(key);
  if (existing) return existing;

  const url = branch ? `/api/skills?branch=${encodeURIComponent(branch)}` : '/api/skills';
  const promise = apiFetch<{ skills: SkillInfo[] }>(url)
    .then((res) => {
      _resultCache.set(key, res.skills);
      _inFlight.delete(key);
      return res.skills;
    })
    .catch((err) => {
      _inFlight.delete(key);
      throw err;
    });
  _inFlight.set(key, promise);
  return promise;
}

/**
 * Fetches the agent's skill registry (with bodies) for the slash-command
 * menu and the chat-rendering chip lookups. Pass ``branch`` to read the
 * PR-preview prefix.
 */
export function useSkills(branch?: string | null) {
  const initial = _resultCache.get(_cacheKey(branch)) ?? [];
  const [skills, setSkills] = useState<SkillInfo[]>(initial);
  const [loading, setLoading] = useState(initial.length === 0);

  useEffect(() => {
    let cancelled = false;
    const cached = _resultCache.get(_cacheKey(branch));
    if (cached) {
      setSkills(cached);
      setLoading(false);
      return;
    }
    setLoading(true);
    _fetchSkills(branch)
      .then((s) => {
        if (!cancelled) {
          setSkills(s);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSkills([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [branch]);

  return { skills, loading };
}
